import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Compass } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const ACCREDITATIONS = [
  { key: 'QLD_L2', label: 'QLD L2' },
  { key: 'NSW_L2', label: 'NSW L2' },
  { key: 'VIC_L2', label: 'VIC L2' },
  { key: 'WA_L2', label: 'WA L2' },
  { key: 'TLIC3010', label: 'TLIC3010' },
  { key: 'TLIF3060', label: 'TLIF3060' },
];
const SERVICES = [
  { key: 'pilot', label: 'Pilot' },
  { key: 'steerer', label: 'Steerer' },
];

const emptyForm = {
  base_suburb: '', base_state: 'NSW',
  available_from: '', available_until: '',
  accreditations: [], services_offered: ['pilot'], preferred_regions: [],
  vehicle_make_model: '', vehicle_rego: '', warning_lights: '',
  preferred_routes: '', notes: '',
  // Additional clearances
  has_msic: false,
  has_federal_police_clearance: false,
  has_defence_clearance: false,
  has_turbines_access: false,
  mine_sites: [],
  port_access_details: '',
  railway_inductions: '',
  aviation_clearances: '',
};

const PilotAvailabilityTab = ({ pilotEmail }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // For agents managing multiple pilots under one email
  const [pilotRegistrations, setPilotRegistrations] = useState([]);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState('');

  useEffect(() => {
    if (!pilotEmail) return;
    fetchListings();
    axios.get(`${API}/pilots/registrations/by-email/${encodeURIComponent(pilotEmail)}`)
      .then(res => {
        const regs = (res.data || []).filter(r => r.status === 'approved');
        setPilotRegistrations(regs);
        if (regs.length > 0) setSelectedRegistrationId(regs[0].id);
      })
      .catch(() => setPilotRegistrations([]));
  }, [pilotEmail]);

  const fetchListings = async () => {
    try {
      const res = await axios.get(`${API}/pilots/availability/my/${encodeURIComponent(pilotEmail)}`);
      setListings(res.data || []);
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };

  const toggleArr = (field, value) => setForm(prev => ({
    ...prev,
    [field]: prev[field].includes(value) ? prev[field].filter(v => v !== value) : [...prev[field], value]
  }));

  // Mine sites management (add/remove free-text tags)
  const [mineSiteInput, setMineSiteInput] = useState('');
  const addMineSite = () => {
    const v = mineSiteInput.trim();
    if (!v || form.mine_sites.includes(v)) { setMineSiteInput(''); return; }
    setForm(prev => ({ ...prev, mine_sites: [...prev.mine_sites, v] }));
    setMineSiteInput('');
  };
  const removeMineSite = (v) => setForm(prev => ({ ...prev, mine_sites: prev.mine_sites.filter(m => m !== v) }));

  const startNew = () => { setEditingId(null); setForm(emptyForm); setShowForm(true); };
  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      base_suburb: row.base_suburb || '', base_state: row.base_state || 'NSW',
      available_from: row.available_from || '', available_until: row.available_until || '',
      accreditations: row.accreditations || [], services_offered: row.services_offered || ['pilot'],
      preferred_regions: row.preferred_regions || [], vehicle_make_model: row.vehicle_make_model || '',
      vehicle_rego: row.vehicle_rego || '', warning_lights: row.warning_lights || '',
      preferred_routes: row.preferred_routes || '', notes: row.notes || '',
      has_msic: !!row.has_msic,
      has_federal_police_clearance: !!row.has_federal_police_clearance,
      has_defence_clearance: !!row.has_defence_clearance,
      has_turbines_access: !!row.has_turbines_access,
      mine_sites: row.mine_sites || [],
      port_access_details: row.port_access_details || '',
      railway_inductions: row.railway_inductions || '',
      aviation_clearances: row.aviation_clearances || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.base_suburb || !form.base_state) {
      toast.error('Base suburb and state are required.');
      return;
    }
    if (!editingId && !selectedRegistrationId) {
      toast.error('Please select which pilot this listing is for.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/pilots/availability/${editingId}`, { ...form, pilot_email: pilotEmail });
        toast.success('Availability listing updated.');
      } else {
        await axios.post(`${API}/pilots/availability`, {
          ...form,
          pilot_email: pilotEmail,
          pilot_code: '',
          pilot_registration_id: selectedRegistrationId,
        });
        toast.success('Availability posted to public map.');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const removeListing = async (row) => {
    if (!window.confirm('Remove this availability listing? It will no longer appear on the public map.')) return;
    try {
      await axios.delete(`${API}/pilots/availability/${row.id}?pilot_email=${encodeURIComponent(pilotEmail)}`);
      toast.success('Listing removed.');
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const toggleFlag = async (row, key) => {
    try {
      await axios.patch(`${API}/pilots/availability/${row.id}/status`, {
        pilot_email: pilotEmail,
        [key]: !row[key],
      });
      fetchListings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Agent overview - only show when >1 pilot managed OR there's any listing */}
      {(pilotRegistrations.length > 0 || listings.length > 0) && (
        <Card className="p-4 bg-gradient-to-r from-[#1e3a5f] to-[#15304f] text-white" data-testid="agent-overview-card">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
            <div>
              <p className="text-2xl font-bold" data-testid="stat-total-pilots">{pilotRegistrations.length}</p>
              <p className="text-xs opacity-80">Pilot{pilotRegistrations.length === 1 ? '' : 's'} you manage</p>
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="stat-verified">{pilotRegistrations.filter(p => p.steerer_verified).length}</p>
              <p className="text-xs opacity-80">Verified steerers</p>
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="stat-listings">{listings.length}</p>
              <p className="text-xs opacity-80">Listing{listings.length === 1 ? '' : 's'} on the map</p>
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid="stat-available-now">{listings.filter(l => l.is_available_now !== false).length}</p>
              <p className="text-xs opacity-80">Available now</p>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 bg-blue-50 border-blue-200">
        <p className="text-sm text-blue-900">
          Post your availability so shippers can find you on the public map.
          <strong> Listing is 100% free.</strong> Your name, phone, email and rego stay private —
          the public only sees your anonymous code (e.g. <em>P-A7B2</em>) plus accreditations and region.
        </p>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#1e3a5f]">My Availability Listings</h3>
        {!showForm && (
          <Button onClick={startNew} className="bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="new-pilot-availability-btn">
            <Plus className="w-4 h-4 mr-1" /> Post Availability
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-4 border-blue-200">
          <h4 className="font-bold text-[#1e3a5f] mb-3">{editingId ? 'Edit' : 'New'} Availability</h4>

          {/* Pilot selector — shown when this email has multiple registered pilots (agent flow) */}
          {!editingId && pilotRegistrations.length > 1 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
              <Label className="text-sm font-semibold">Which pilot is this listing for? *</Label>
              <select
                value={selectedRegistrationId}
                onChange={(e) => setSelectedRegistrationId(e.target.value)}
                className="w-full p-2 border rounded mt-1"
                data-testid="pilot-registration-select"
              >
                {pilotRegistrations.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.steerer_verified ? ' — ✓ Verified Steerer' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-amber-800 mt-1">You have {pilotRegistrations.length} pilots registered under this email. Each availability listing represents one individual pilot on the public map.</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Base Suburb *</Label>
              <Input value={form.base_suburb} onChange={e => setForm({...form, base_suburb: e.target.value})} placeholder="e.g. Newcastle" data-testid="base-suburb"/>
            </div>
            <div>
              <Label>Base State *</Label>
              <select value={form.base_state} onChange={e => setForm({...form, base_state: e.target.value})} className="w-full p-2 border rounded" data-testid="base-state">
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>Available From</Label>
              <Input type="date" value={form.available_from} onChange={e => setForm({...form, available_from: e.target.value})}/>
            </div>
            <div>
              <Label>Available Until</Label>
              <Input type="date" value={form.available_until} onChange={e => setForm({...form, available_until: e.target.value})}/>
            </div>
            <div className="md:col-span-2">
              <Label>Services Offered</Label>
              <div className="flex gap-3 flex-wrap mt-1">
                {SERVICES.map(s => (
                  <label key={s.key} className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={form.services_offered.includes(s.key)} onChange={() => toggleArr('services_offered', s.key)} /> {s.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Accreditations Held</Label>
              <div className="flex gap-3 flex-wrap mt-1">
                {ACCREDITATIONS.map(a => (
                  <label key={a.key} className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={form.accreditations.includes(a.key)} onChange={() => toggleArr('accreditations', a.key)} /> {a.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Preferred Regions</Label>
              <div className="flex gap-3 flex-wrap mt-1">
                {STATES.map(s => (
                  <label key={s} className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={form.preferred_regions.includes(s)} onChange={() => toggleArr('preferred_regions', s)} /> {s}
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Clearances & Access */}
            <div className="md:col-span-2 border-t pt-4 mt-2">
              <h4 className="font-semibold text-[#1e3a5f] mb-2">Additional Clearances &amp; Access</h4>
              <div className="grid md:grid-cols-2 gap-2 mb-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_msic} onChange={(e) => setForm({...form, has_msic: e.target.checked})} data-testid="chk-msic"/>
                  MSIC (Maritime Security ID Card)
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_federal_police_clearance} onChange={(e) => setForm({...form, has_federal_police_clearance: e.target.checked})} data-testid="chk-fpc"/>
                  Current Federal Police Clearance
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_defence_clearance} onChange={(e) => setForm({...form, has_defence_clearance: e.target.checked})} data-testid="chk-defence"/>
                  Defence Site Access Clearances
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.has_turbines_access} onChange={(e) => setForm({...form, has_turbines_access: e.target.checked})} data-testid="chk-turbines"/>
                  Turbines Access User
                </label>
              </div>

              {/* Mine sites - multi tag input */}
              <div className="mb-3">
                <Label>Mine Site Access — add each site you&apos;re inducted for</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={mineSiteInput}
                    onChange={(e) => setMineSiteInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMineSite(); } }}
                    placeholder="e.g. BHP Olympic Dam"
                    data-testid="mine-site-input"
                  />
                  <Button type="button" onClick={addMineSite} className="bg-[#1e3a5f] hover:bg-[#15304f] text-white" data-testid="add-mine-site-btn">Add</Button>
                </div>
                {form.mine_sites.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.mine_sites.map(s => (
                      <span key={s} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {s}
                        <button type="button" onClick={() => removeMineSite(s)} className="text-blue-900 hover:text-red-600 ml-1" aria-label={`Remove ${s}`}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid md:grid-cols-1 gap-3">
                <div>
                  <Label>Port Access — specify ports</Label>
                  <Input value={form.port_access_details} onChange={e => setForm({...form, port_access_details: e.target.value})} placeholder="e.g. Port of Brisbane, Port Botany" data-testid="port-access"/>
                </div>
                <div>
                  <Label>Railway Inductions (ARTC / other specific)</Label>
                  <Input value={form.railway_inductions} onChange={e => setForm({...form, railway_inductions: e.target.value})} placeholder="e.g. ARTC Cat 1 &amp; 2, Metro Trains VIC" data-testid="rail-inductions"/>
                </div>
                <div>
                  <Label>Aviation Clearances</Label>
                  <Input value={form.aviation_clearances} onChange={e => setForm({...form, aviation_clearances: e.target.value})} placeholder="e.g. ASIC (Aviation Security ID Card), Sydney Airport" data-testid="aviation-clearances"/>
                </div>
              </div>
            </div>

            <div>
              <Label>Vehicle (make/model)</Label>
              <Input value={form.vehicle_make_model} onChange={e => setForm({...form, vehicle_make_model: e.target.value})} placeholder="e.g. Toyota Hilux 4x4"/>
            </div>
            <div>
              <Label>Rego (private)</Label>
              <Input value={form.vehicle_rego} onChange={e => setForm({...form, vehicle_rego: e.target.value})} placeholder="ABC-123 — not shown publicly"/>
            </div>
            <div className="md:col-span-2">
              <Label>Warning Lights / Equipment</Label>
              <Input value={form.warning_lights} onChange={e => setForm({...form, warning_lights: e.target.value})} placeholder="e.g. Amber roof beacon + LED bar"/>
            </div>
            <div className="md:col-span-2">
              <Label>Preferred Routes</Label>
              <Input value={form.preferred_routes} onChange={e => setForm({...form, preferred_routes: e.target.value})} placeholder="e.g. Newcastle → Brisbane corridor"/>
            </div>
            <div className="md:col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Anything else shippers should know" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={submit} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-availability-btn">
              {saving ? 'Saving...' : (editingId ? 'Update' : 'Post to Map')}
            </Button>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-gray-600">Loading...</p>
      ) : listings.length === 0 ? (
        <Card className="p-8 text-center text-gray-600">
          <Compass className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <p>No availability posted yet. Click <strong>Post Availability</strong> to appear on the public map.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {listings.map(row => (
            <Card key={row.id} className="p-4" data-testid={`pilot-listing-${row.id}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <p className="font-semibold text-[#1e3a5f]">{row.pilot_code} · {row.base_suburb}, {row.base_state}</p>
                  <p className="text-xs text-gray-500">
                    {row.available_from ? `${row.available_from} → ${row.available_until || 'ongoing'}` : 'Ongoing'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => startEdit(row)}><Edit2 className="w-3.5 h-3.5"/></Button>
                  <Button size="sm" variant="outline" className="border-red-500 text-red-600" onClick={() => removeListing(row)}><Trash2 className="w-3.5 h-3.5"/></Button>
                </div>
              </div>
              {row.accreditations?.length > 0 && (
                <p className="text-xs text-gray-700 mb-1">Accreditations: {row.accreditations.join(', ')}</p>
              )}
              {row.services_offered?.length > 0 && (
                <p className="text-xs text-gray-700 mb-2">Services: {row.services_offered.join(', ')}</p>
              )}

              {/* Quick toggles */}
              <div className="flex flex-wrap gap-3 pt-2 mt-2 border-t border-dashed">
                <button
                  type="button"
                  onClick={() => toggleFlag(row, 'is_available_now')}
                  className={`flex items-center gap-2 text-xs px-2 py-1 rounded transition-colors ${
                    row.is_available_now !== false ? 'bg-green-50 text-green-800 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`toggle-available-${row.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${row.is_available_now !== false ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                  {row.is_available_now !== false ? 'Available now' : 'Marked busy'}
                </button>
                <button
                  type="button"
                  onClick={() => toggleFlag(row, 'always_visible')}
                  className={`flex items-center gap-2 text-xs px-2 py-1 rounded transition-colors ${
                    row.always_visible ? 'bg-blue-50 text-blue-800 hover:bg-blue-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`toggle-always-${row.id}`}
                >
                  <span className={`w-2 h-2 rounded-full ${row.always_visible ? 'bg-blue-500' : 'bg-gray-400'}`}></span>
                  {row.always_visible ? 'Always visible on map' : 'Hidden after expiry'}
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PilotAvailabilityTab;
